/**
 * Departments Page Component
 * Manages departments for the selected outlet
 */

import { Loader2, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useCreateDepartment, useDepartments } from '@hooks/menu';
import { useAppSelector } from '../../../hooks/useRedux';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import { selectCanManageDepartments } from '../../../store/auth/authSelectors';
import { selectEffectiveOutletId } from '../../../store/outlets';
import { CreateDepartmentRequest } from '../../../types/department';
import {
  createTourElementResolver,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import { ReadOnlyAccessNotice } from '../../common';
import { OutletIndicator } from '../../common/OutletIndicator';
import { DepartmentFormModal } from './DepartmentFormModal';
import { DepartmentsTable } from './DepartmentsTable';
import { EmptyDepartments } from './EmptyDepartments';

export const DepartmentsPage: React.FC = () => {
  const outletId = useAppSelector(selectEffectiveOutletId);
  const canManageDepartments = useAppSelector(selectCanManageDepartments);

  const { data: departments = [], isLoading } = useDepartments(outletId);
  const createDepartment = useCreateDepartment();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.departmentsPage,
        ),
        popover: {
          title: 'Create departments first',
          description:
            'Departments are important because menu items and kitchen printer routing depend on them.',
          side: 'bottom' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.departmentsAddButton),
          onboardingTourSelector(
            ONBOARDING_TOUR_TARGETS.departmentsEmptyAddButton,
          ),
        ]),
        popover: {
          title: 'Add a department',
          description:
            'Start by creating the core departments your restaurant operates with, like kitchen, bar, or takeaway.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.departmentsTable),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.departmentsPage),
        ]),
        popover: {
          title: 'Review and manage departments',
          description:
            'Once departments are created, they appear here so you can review them and keep the outlet setup organized.',
          side: 'top' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    stepId: 'departments',
    steps: onboardingTourSteps,
  });

  const handleCreateDepartment = (data: CreateDepartmentRequest) => {
    if (!outletId || !canManageDepartments) return;
    createDepartment.mutate(
      { ...data, outlet_id: outletId },
      { onSuccess: () => setIsFormModalOpen(false) },
    );
  };

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.departmentsPage}
    >
      <OutletIndicator description="Viewing departments across all outlets. Select a specific outlet to add a new department." />

      {!canManageDepartments ? (
        <ReadOnlyAccessNotice message="You can view departments here, but only authorized roles can create or update them." />
      ) : null}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Departments</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage departments for menu item routing
          </p>
        </div>
        {departments.length > 0 && canManageDepartments && (
          <button
            type="button"
            onClick={() => setIsFormModalOpen(true)}
            disabled={!outletId}
            data-onboarding-tour={ONBOARDING_TOUR_TARGETS.departmentsAddButton}
            className="flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={20} />
            <span>Add Department</span>
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && departments.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-pos-primary" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && departments.length === 0 && (
        <EmptyDepartments
          canAdd={canManageDepartments}
          onAdd={() =>
            outletId && canManageDepartments && setIsFormModalOpen(true)
          }
        />
      )}

      {!isLoading && departments.length > 0 && (
        <DepartmentsTable departments={departments} />
      )}

      {/* Form Modal */}
      <DepartmentFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleCreateDepartment}
        outletId={outletId}
        isLoading={createDepartment.isPending}
      />
    </div>
  );
};
